import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Sidebar from '../../components/feature/Sidebar';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { articles } from '../../mocks/articles';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();

  const categoryMap: Record<string, string> = {
    'fifa-online-4': 'FIFA Online 4',
    'age-of-empires': 'Age of Empires',
    'lien-minh-huyen-thoai': 'Liên Minh Huyền Thoại',
    'csgo': 'CS:GO',
    'pubg': 'PUBG',
    'tin-game': 'Tin Game'
  };

  const categoryName = categoryMap[slug || ''] || 'Danh Mục';
  
  // Sửa logic lọc bài viết để khớp với dữ liệu
  const categoryArticles = articles.filter(article => {
    const currentPath = window.location.pathname.replace('/', '');
    
    // Xử lý riêng cho từng chuyên mục
    if (currentPath === 'fifa-online-4') {
      return article.game === 'FIFA Online 4';
    }
    if (currentPath === 'age-of-empires') {
      return article.game === 'Age of Empires';
    }
    if (currentPath === 'lien-minh-huyen-thoai') {
      return article.game === 'Liên Minh Huyền Thoại';
    }
    if (currentPath === 'csgo') {
      return article.game === 'CS:GO';
    }
    if (currentPath === 'pubg') {
      return article.game === 'PUBG';
    }
    if (currentPath === 'tin-game') {
      return article.game === 'Tin Game';
    }
    
    return false;
  });

  const breadcrumbItems = [
    { label: 'Trang chủ', href: '/' },
    { label: categoryName }
  ];

  // Update document metadata using React 19 approach
  useEffect(() => {
    document.title = `${categoryName} - Tin tức, hướng dẫn và game thủ | Gametv.asia`;
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', `Tổng hợp tin tức, hướng dẫn chơi game và thông tin về các game thủ ${categoryName} hàng đầu Việt Nam. Cập nhật liên tục 24/7.`);
    updateMetaTag('keywords', `${categoryName}, tin tức game, hướng dẫn, game thủ, esports, việt nam`);
    
    // Open Graph tags
    updateMetaTag('og:title', `${categoryName} - Tin tức và hướng dẫn | Gametv.asia`, true);
    updateMetaTag('og:description', `Tổng hợp tin tức, hướng dẫn chơi game và thông tin về các game thủ ${categoryName} hàng đầu Việt Nam.`, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', `https://gametv.asia/${slug}`, true);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://gametv.asia/${slug}`);

    // Add Schema markup
    let schemaScript = document.querySelector('script[type="application/ld+json"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": categoryName,
      "description": `Tổng hợp tin tức, hướng dẫn chơi game và thông tin về các game thủ ${categoryName}`,
      "url": `https://gametv.asia/${slug}`,
      "publisher": {
        "@type": "Organization",
        "name": "Gametv.asia",
        "logo": {
          "@type": "ImageObject",
          "url": "https://gametv.asia/logo.png"
        }
      }
    });
  }, [categoryName, slug]);

  return (
    <div className="min-h-screen bg-dark-bg">
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-[1600px] mx-auto px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Category Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12 mt-8"
          >
            <h1 className="font-exo font-black text-4xl md:text-5xl text-white mb-4">
              {categoryName}
            </h1>
            <p className="text-gray-400 text-lg">
              {categoryArticles.length} bài viết
            </p>
          </motion.div>

          <div className="flex gap-8">
            {/* Articles Grid */}
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categoryArticles.map((article, index) => (
                  <motion.article
                    key={article.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-dark-card rounded-2xl overflow-hidden border border-gray-800 hover:border-accent-cyan transition-all duration-300"
                  >
                    <Link to={`/bai-viet/${article.slug}`}>
                      {/* Image */}
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-accent-cyan/90 text-dark-bg text-xs font-bold rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h2 className="font-exo font-bold text-xl text-white mb-3 group-hover:text-accent-cyan transition-colors line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                          {article.excerpt}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-2">
                            <img
                              src={article.author.avatar}
                              alt={article.author.name}
                              className="w-6 h-6 rounded-full"
                            />
                            <span>{article.author.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span>{article.readTime}</span>
                            <span>•</span>
                            <span>{article.views}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>

              {categoryArticles.length === 0 && (
                <div className="text-center py-20">
                  <p className="text-gray-400 text-lg">Chưa có bài viết nào trong danh mục này</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="hidden xl:block">
              <div className="sticky top-24">
                <Sidebar />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CategoryPage;
